/**
 * GrowthChart
 *
 * Displays baby growth data with WHO percentile curves.
 * Uses SVG for a custom, responsive chart without external dependencies.
 */

import { useMemo } from 'react';
import { useTranslation } from '@parentpath/i18n';
import { BabyGrowthEntry } from '@/services/babyGrowthService';
import {
  Gender,
  MeasurementType,
  getChartData,
  getMeasurementInfo,
} from '../data/whoGrowthStandards';

interface GrowthChartProps {
  entries: BabyGrowthEntry[];
  type: MeasurementType;
  gender: Gender;
  birthDate: string;
  maxAgeInDays?: number;
}

// Chart dimensions
const CHART_WIDTH = 340;
const CHART_HEIGHT = 200;
const PADDING = { top: 20, right: 15, bottom: 35, left: 45 };
const INNER_WIDTH = CHART_WIDTH - PADDING.left - PADDING.right;
const INNER_HEIGHT = CHART_HEIGHT - PADDING.top - PADDING.bottom;

export function GrowthChart({
  entries,
  type,
  gender,
  birthDate,
  maxAgeInDays = 365,
}: GrowthChartProps) {
  const { t } = useTranslation('babyGrowth');

  // Get chart data based on gender and type
  const chartData = useMemo(() => {
    return getChartData(gender, type, maxAgeInDays);
  }, [gender, type, maxAgeInDays]);

  // Get measurement info
  const measurementInfo = getMeasurementInfo(type);

  // Calculate age in days for each entry
  const dataPoints = useMemo(() => {
    if (!birthDate) return [];

    const birthDateObj = new Date(birthDate);
    return entries
      .map((entry) => {
        const measureDate = new Date(entry.measured_at);
        const ageInDays = Math.floor((measureDate.getTime() - birthDateObj.getTime()) / (1000 * 60 * 60 * 24));

        let value: number | null = null;
        if (type === 'weight') value = entry.weight_grams;
        else if (type === 'height') value = entry.height_cm ? Number(entry.height_cm) : null;
        else if (type === 'head') value = entry.head_circumference_cm ? Number(entry.head_circumference_cm) : null;

        return { ageInDays, value, entry };
      })
      .filter((d) => d.value !== null && d.ageInDays >= 0 && d.ageInDays <= maxAgeInDays)
      .sort((a, b) => a.ageInDays - b.ageInDays);
  }, [entries, type, birthDate, maxAgeInDays]);

  // Calculate scales
  const { xScale, yScale, yMin, yMax } = useMemo(() => {
    // X scale: 0 to maxAgeInDays
    const xScale = (age: number) => (age / maxAgeInDays) * INNER_WIDTH;

    // Y scale: find min/max from percentile data and actual values
    const allValues = [
      ...chartData.p3,
      ...chartData.p97,
      ...dataPoints.map((d) => d.value as number),
    ];

    let yMin = Math.min(...allValues) * 0.95;
    let yMax = Math.max(...allValues) * 1.05;

    // Round to nice values
    if (type === 'weight') {
      yMin = Math.floor(yMin / 500) * 500;
      yMax = Math.ceil(yMax / 500) * 500;
    } else {
      yMin = Math.floor(yMin);
      yMax = Math.ceil(yMax);
    }

    const yScale = (value: number) => INNER_HEIGHT - ((value - yMin) / (yMax - yMin)) * INNER_HEIGHT;

    return { xScale, yScale, yMin, yMax };
  }, [chartData, dataPoints, maxAgeInDays, type]);

  // Generate path for percentile line
  const createPath = (values: number[]) => {
    const points = chartData.agePoints.map((age, i) => {
      const x = xScale(age);
      const y = yScale(values[i]);
      return `${x},${y}`;
    });
    return `M${points.join(' L')}`;
  };

  // Generate Y-axis ticks
  const yTicks = useMemo(() => {
    const range = yMax - yMin;
    let step = type === 'weight' ? 1000 : 5;

    if (type === 'weight') {
      if (range > 10000) step = 2000;
      else if (range > 5000) step = 1000;
      else step = 500;
    } else {
      if (range > 30) step = 10;
      else if (range > 15) step = 5;
      else step = 2;
    }

    const ticks: number[] = [];
    for (let v = Math.ceil(yMin / step) * step; v <= yMax; v += step) {
      ticks.push(v);
    }
    return ticks;
  }, [yMin, yMax, type]);

  // Generate X-axis ticks
  const xTicks = useMemo(() => {
    const ticks: { age: number; label: string }[] = [];

    const birthLabel = t('chart.birth');
    if (maxAgeInDays <= 60) {
      // Weekly ticks for first 2 months
      for (let week = 0; week <= maxAgeInDays / 7; week += 2) {
        const age = week * 7;
        ticks.push({ age, label: week === 0 ? birthLabel : `W${week}` });
      }
    } else if (maxAgeInDays <= 365) {
      // Monthly ticks for first year
      for (let month = 0; month <= maxAgeInDays / 30; month += 2) {
        const age = Math.round(month * 30.44);
        ticks.push({ age, label: month === 0 ? birthLabel : `M${month}` });
      }
    } else {
      // Quarterly ticks for 2 years
      for (let month = 0; month <= 24; month += 3) {
        const age = Math.round(month * 30.44);
        ticks.push({ age, label: month === 0 ? birthLabel : `M${month}` });
      }
    }

    return ticks;
  }, [maxAgeInDays, t]);

  // Handle empty state
  if (dataPoints.length === 0) {
    return (
      <div className="rounded-[15px] flex items-center justify-center h-48">
        <p className="text-white/60 text-sm text-center whitespace-pre-line">
          {t('chart.empty')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[15px]">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="w-full h-auto"
      >
        {/* Chart area */}
        <g transform={`translate(${PADDING.left}, ${PADDING.top})`}>
          {/* Grid lines */}
          {yTicks.map((tick) => (
            <line
              key={tick}
              x1={0}
              x2={INNER_WIDTH}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke="rgba(255,255,255,0.1)"
              strokeDasharray="2,2"
            />
          ))}

          {/* Percentile area fill (3-97) - green background for normal range */}
          <path
            d={`
              ${createPath(chartData.p3)}
              L${xScale(chartData.agePoints[chartData.agePoints.length - 1])},${yScale(chartData.p97[chartData.p97.length - 1])}
              ${chartData.agePoints.slice().reverse().map((age, i) => {
                const revIndex = chartData.agePoints.length - 1 - i;
                return `L${xScale(age)},${yScale(chartData.p97[revIndex])}`;
              }).join(' ')}
              Z
            `}
            fill="rgba(52, 211, 153, 0.15)"
          />

          {/* Data line - always show as continuous line */}
          {dataPoints.length >= 1 && (
            <path
              d={`M${dataPoints.map(d => `${xScale(d.ageInDays)},${yScale(d.value as number)}`).join(' L')}`}
              stroke="#F59E11"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* X-axis */}
          <line x1={0} x2={INNER_WIDTH} y1={INNER_HEIGHT} y2={INNER_HEIGHT} stroke="rgba(255,255,255,0.3)" />

          {/* X-axis ticks */}
          {xTicks.map(({ age, label }) => (
            <g key={age} transform={`translate(${xScale(age)}, ${INNER_HEIGHT})`}>
              <line y1={0} y2={4} stroke="rgba(255,255,255,0.3)" />
              <text y={16} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">
                {label}
              </text>
            </g>
          ))}

          {/* Y-axis */}
          <line x1={0} x2={0} y1={0} y2={INNER_HEIGHT} stroke="rgba(255,255,255,0.3)" />

          {/* Y-axis ticks */}
          {yTicks.map((tick) => (
            <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
              <line x1={-4} x2={0} stroke="rgba(255,255,255,0.3)" />
              <text x={-8} textAnchor="end" alignmentBaseline="middle" fill="rgba(255,255,255,0.5)" fontSize="9">
                {type === 'weight' ? (tick >= 1000 ? `${(tick / 1000).toFixed(tick % 1000 === 0 ? 0 : 1)}kg` : tick) : tick}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-pp-orange rounded" />
          <span className="text-white/60">{measurementInfo.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-emerald-400/20 rounded" />
          <span className="text-white/60">P3–P97</span>
        </div>
      </div>
    </div>
  );
}
